import {
  Dropdown,
  DropdownChangeEvent,
  DropdownFilterEvent,
} from "primereact/dropdown";
import { ControllerRenderProps, FieldValues } from "react-hook-form";
import "primereact/resources/themes/saga-blue/theme.css";
import { useRef } from "react";
import { CONTAINS_NO_WHITESPACE } from "./dropdownFilterMatchMode";

interface IAppDropDownProps<TOption> {
  field?: ControllerRenderProps<FieldValues, string>;
  options: TOption[] | undefined;
  disabled?: boolean;
  className?: string;
  filter?: boolean;
  editable?: boolean;
  value?: number;
  width?: string;
  invalid?: boolean;
  defaultValue?: string;
  includeId?: boolean;
  filterBy?: string;
  emptyMessage?: string;
  optionDisabled?: string;
  onChange?: (value: number | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onFilter?: (event: DropdownFilterEvent) => TOption[] | undefined;
}

export const AppDropDown = <TOption,>({
  field,
  options,
  disabled,
  className,
  filter,
  editable,
  value,
  width,
  invalid,
  defaultValue,
  includeId = true,
  filterBy = "name,id",
  emptyMessage = "Няма намерени опции",
  optionDisabled,
  onChange,
  onFocus,
  onBlur,
  onFilter,
}: IAppDropDownProps<TOption>) => {
  const processingRef = useRef(false);

  const onChangeHandler = (e: DropdownChangeEvent) => {
    // Prevent concurrent processing
    if (processingRef.current) {
      return;
    }

    processingRef.current = true;

    const newValue = e.value === undefined ? null : e.value;

    // Use queueMicrotask to ensure sequential processing
    queueMicrotask(() => {
      try {
        if (onChange) {
          onChange(newValue);
        } else if (field) {
          field.onChange(newValue);
        }
      } finally {
        processingRef.current = false;
      }
    });
  };

  return (
    <Dropdown
      id={field?.name}
      value={value ?? field?.value}
      panelStyle={{ width: "auto", minWidth: width, maxWidth: "300px" }}
      onChange={(e) => onChangeHandler(e)}
      filterClearIcon={false}
      options={options}
      className={className}
      defaultValue={defaultValue}
      disabled={disabled}
      editable={editable}
      optionLabel="name"
      optionValue="id"
      optionDisabled={optionDisabled}
      invalid={invalid}
      filter={filter}
      filterMatchMode={CONTAINS_NO_WHITESPACE}
      onFilter={onFilter}
      emptyMessage={emptyMessage}
      onFocus={onFocus}
      onBlur={onBlur}
      filterBy={filterBy}
      appendTo={document.body}
      itemTemplate={(option) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.4" }}>
          {option.name}
          <span
            style={{
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: "0.85em",
              marginLeft: "5px",
            }}
          >
            {includeId && !!option.id ? `(#${option.id})` : ""}
          </span>
        </div>
      )}
    />
  );
};
